/** Staff RBAC catalog — mirrors public.permission_resource / permission_action */

export const PERMISSION_ACTIONS = ['view', 'create', 'update', 'delete'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/**
 * Resources shown in the roles matrix and used by hasPermission().
 * Kept aligned with the current main menu + settings IA.
 * (DB enum may still contain legacy values like `reports`.)
 */
export const PERMISSION_RESOURCES = [
  // Main / clinical
  'patients',
  'patients_cpr',
  'appointments',
  'staff_calendars',
  'referrals',
  'documents',
  'journal',
  'dental',
  // Finance (Economy + finance settings)
  'invoices',
  'payments',
  'quotes',
  'punch_cards',
  // Integrations hub
  'questionnaires',
  'sms',
  'sygeforsikring',
  // Settings / admin
  'settings_general',
  'settings_users',
  'settings_roles',
  'settings_billing',
  'online_booking',
  'multi_clinic',
  'audit_logs',
] as const;

export type PermissionResource = (typeof PERMISSION_RESOURCES)[number];

/** Compact map baked into JWT membership claims: { patients: ['view','create'], ... } */
export type PermissionMap = Partial<Record<PermissionResource, PermissionAction[]>>;

/** Matrix UI groups — mirrors sidebar + settings navigation structure */
export const PERMISSION_RESOURCE_GROUPS: Array<{
  groupKey: string;
  resources: PermissionResource[];
}> = [
  {
    groupKey: 'main',
    resources: [
      'patients',
      'patients_cpr',
      'appointments',
      'staff_calendars',
      'referrals',
      'documents',
      'journal',
      'dental',
    ],
  },
  {
    groupKey: 'finance',
    resources: ['invoices', 'payments', 'quotes', 'punch_cards'],
  },
  {
    groupKey: 'integrations',
    resources: ['questionnaires', 'sms', 'sygeforsikring'],
  },
  {
    groupKey: 'settings',
    resources: [
      'settings_general',
      'settings_users',
      'settings_roles',
      'settings_billing',
      'online_booking',
      'multi_clinic',
      'audit_logs',
    ],
  },
];

/** Resources that grant access to the Economy section (any view). */
export const ECONOMY_PERMISSION_RESOURCES: PermissionResource[] = [
  'invoices',
  'payments',
  'quotes',
];

/** Resources that grant access to the Integrations settings hub (any view). */
export const INTEGRATIONS_PERMISSION_RESOURCES: PermissionResource[] = [
  'payments',
  'questionnaires',
  'sms',
  'sygeforsikring',
  'settings_general',
];

/** Map sidebar / routes to permission resources for view gating */
export const ROUTE_PERMISSION_RESOURCE: Record<string, PermissionResource> = {
  '/patients': 'patients',
  '/henvisninger': 'referrals',
  '/care-communications': 'patients',
  '/appointments': 'appointments',
  '/documents': 'documents',
  '/economy/invoices': 'invoices',
  '/economy/payments': 'payments',
  '/economy/quotes': 'quotes',
  '/economy': 'invoices',
  '/settings/sms': 'sms',
  '/settings/payments': 'payments',
  '/settings/questionnaires': 'questionnaires',
  '/settings/sygeforsikring': 'sygeforsikring',
};

export function permissionMapHas(
  map: PermissionMap | null | undefined,
  resource: PermissionResource,
  action: PermissionAction,
): boolean {
  if (!map) return false;
  const actions = map[resource];
  return Array.isArray(actions) && actions.includes(action);
}

export function emptyPermissionMap(): PermissionMap {
  const map: PermissionMap = {};
  for (const resource of PERMISSION_RESOURCES) {
    map[resource] = [];
  }
  return map;
}

export function normalizePermissionMap(raw: unknown): PermissionMap {
  const map = emptyPermissionMap();
  if (!raw || typeof raw !== 'object') return map;
  const obj = raw as Record<string, unknown>;
  for (const resource of PERMISSION_RESOURCES) {
    const actions = obj[resource];
    if (!Array.isArray(actions)) continue;
    map[resource] = actions.filter(
      (a): a is PermissionAction =>
        typeof a === 'string' && (PERMISSION_ACTIONS as readonly string[]).includes(a),
    );
  }
  return map;
}
